// Bluetooth trainer: FTMS (power, cadence, speed; grade/wind/rolling-resistance simulation, ERG or resistance level),
// Cycling Power fallback for trainers that only measure.
import { ROAD_CRR, gradeToResistance, type TrainerCommand } from '@gruvetto/core';
import { t } from '../i18n/index.js';
import { BleDevice, valueOf, type GattCharacteristic, type GattServer, type StatusFn } from './ble.js';
import { UUID, FTMS_OP, ALL_CAPS, CyclingPowerParser, parseIndoorBikeData, parseFtmsFeature, parseRange, encodeSim, encodeErg, encodeResistance, type FtmsCaps, type Range, type Reading } from './gatt.js';

export { needBluetooth } from './ble.js';

/** Longest a control-point write may take: past it the write counts as failed, so a stalled link cannot block the next ones. */
export const WRITE_TIMEOUT_MS = 3000;

/** Write queue: one GATT operation at a time, each given up after WRITE_TIMEOUT_MS. */
class Queue {
  private p: Promise<unknown> = Promise.resolve();
  run<T>(fn: () => Promise<T>): Promise<T> {
    const bounded = () => new Promise<T>((ok, ko) => {
      const timer = setTimeout(() => ko(new Error('FTMS: the trainer did not answer')), WRITE_TIMEOUT_MS);
      fn().then(v => { clearTimeout(timer); ok(v); }, e => { clearTimeout(timer); ko(e); });
    });
    const r = this.p.then(bounded, bounded);
    this.p = r.catch(() => {});
    return r;
  }
}

export class Trainer extends BleDevice {
  kind: 'FTMS' | 'power' | null = null;
  caps: FtmsCaps = { sim: false, erg: false, resistance: false };
  private cp: GattCharacteristic | null = null;
  private powerRange: Range | null = null;
  private resistanceRange: Range | null = null;
  private q = new Queue();
  private mode: 'sim' | 'erg' | 'res' | null = null;
  /** Paused by a release on a trainer with ERG only: it must be resumed before the next target. */
  private paused = false;
  private last = { grade: null as number | null, wind: 0, crr: ROAD_CRR, erg: null as number | null, res: null as number | null, at: 0 };
  private cps = new CyclingPowerParser(2105);

  constructor(private readonly onData: (d: Reading) => void, onStatus: StatusFn) { super(onStatus); }

  set wheelMm(mm: number) { this.cps.wheelMm = mm; }
  get controllable() { return !!this.cp && this.connected; }
  /** The trainer imposes power (ERG mode). */
  get canErg() { return this.controllable && this.caps.erg; }
  /** The trainer simulates the grade (simulation mode, or resistance level as a fallback). */
  get canSim() { return this.controllable && (this.caps.sim || this.caps.resistance); }

  protected fallbackName() { return t.devices.trainer; }
  protected filters() { return { filters: [{ services: [UUID.ftms] }, { services: [UUID.cps] }], optionalServices: [UUID.ftms, UUID.cps, 'device_information'] }; }
  protected label() { return `${this.name} · ${this.kind === 'power' ? t.devices.powerMeter : 'FTMS'}${this.controllable ? t.devices.controlled : ''}`; }
  protected detach() { this.cp = null; this.mode = null; this.paused = false; this.last.at = 0; this.cps.reset(); }

  protected async attach(srv: GattServer) {
    let ftms;
    try { ftms = await srv.getPrimaryService(UUID.ftms); } catch { ftms = null; }
    if (!ftms) {
      const m = await (await srv.getPrimaryService(UUID.cps)).getCharacteristic(UUID.cpsMeasurement);
      m.addEventListener('characteristicvaluechanged', e => this.onData(this.cps.parse(valueOf(e), performance.now())));
      await m.startNotifications();
      this.kind = 'power';
      return;
    }
    const ibd = await ftms.getCharacteristic(UUID.indoorBikeData);
    ibd.addEventListener('characteristicvaluechanged', e => this.onData(parseIndoorBikeData(valueOf(e))));
    await ibd.startNotifications();
    this.kind = 'FTMS';
    // capabilities and ranges: optional, some trainers do not expose them
    const read = async (uuid: number) => { try { return await (await ftms.getCharacteristic(uuid)).readValue(); } catch { return null; } };
    const feat = await read(UUID.ftmsFeature);
    this.caps = feat ? parseFtmsFeature(feat) : { ...ALL_CAPS };
    const pr = await read(UUID.powerRange), rr = await read(UUID.resistanceRange);
    this.powerRange = pr && parseRange(pr);
    this.resistanceRange = rr && parseRange(rr);
    try {
      const cp = await ftms.getCharacteristic(UUID.ftmsControlPoint);
      cp.addEventListener('characteristicvaluechanged', e => this.onCpResponse(valueOf(e)));
      await cp.startNotifications();
      this.cp = cp;
      await this.write([FTMS_OP.requestControl]);
      await this.write([FTMS_OP.start]);
    } catch (e) { console.warn('FTMS: control point unavailable', e); this.cp = null; }
  }

  private write(bytes: ArrayLike<number>) {
    const cp = this.cp;
    if (!cp) return Promise.resolve();
    return this.q.run(() => cp.writeValueWithResponse(new Uint8Array(bytes)));
  }
  private onCpResponse(v: DataView) { if (v.getUint8(0) === FTMS_OP.response && v.getUint8(2) !== 0x01) console.warn('FTMS: operation rejected', v.getUint8(1), 'code', v.getUint8(2)); }

  /** Effort governor command (core/effort.ts): the only entry point from races to resistance. */
  apply(cmd: TrainerCommand) { if (cmd.mode === 'erg') this.setErg(cmd.watts); else this.setSim(cmd.grade, cmd.wind, cmd.crr); }
  /** Resistance reset to flat (pause, end of race). A trainer with ERG only has no flat: it is paused, which frees the rider. */
  release() {
    if (!this.controllable) return;
    if (this.caps.sim || this.caps.resistance) return this.setSim(0, 0, ROAD_CRR, true);
    if (this.mode !== 'erg') return;
    this.mode = null; this.last.erg = null; this.paused = true;
    this.write([FTMS_OP.stop, 0x02]).catch(e => console.warn('release', e));
  }
  setGrade(pct: number) { this.setSim(pct, 0, ROAD_CRR); }

  /** Felt grade: simulation mode if possible (at most every 400 ms, only when it changes), otherwise resistance level. */
  setSim(pct: number, wind = 0, crr = ROAD_CRR, force = false) {
    if (!this.controllable) return;
    if (!this.caps.sim) { if (this.caps.resistance) this.setResistance(gradeToResistance(pct), force); return; }
    const now = performance.now(), L = this.last;
    const same = this.mode === 'sim' && L.grade !== null && Math.abs(pct - L.grade) < 0.2 && Math.abs(wind - L.wind) < 0.3 && Math.abs(crr - L.crr) < 0.0005;
    if (!force && same && now - L.at < 3000) return;
    if (!force && now - L.at < 400) return;
    this.mode = 'sim'; L.grade = pct; L.wind = wind; L.crr = crr; L.at = now;
    this.write(encodeSim(wind, pct, crr)).catch(e => console.warn('setSim', e));
  }

  private setResistance(frac: number, force: boolean) {
    const now = performance.now(), L = this.last, lvl = Math.round(frac * 50) / 50;
    if (!force && this.mode === 'res' && L.res === lvl && now - L.at < 4000) return;
    if (!force && now - L.at < 1000) return;
    this.mode = 'res'; L.res = lvl; L.at = now;
    this.write(encodeResistance(lvl, this.resistanceRange)).catch(e => console.warn('setResistance', e));
  }

  /** ERG mode: the trainer imposes the target power whatever the cadence. */
  setErg(watts: number) {
    if (!this.canErg) return;
    const now = performance.now(), L = this.last, w = Math.max(0, Math.round(watts));
    if (this.mode === 'erg' && L.erg === w && now - L.at < 4000) return;
    if (now - L.at < 300) return;
    this.mode = 'erg'; L.erg = w; L.at = now;
    if (this.paused) { this.paused = false; this.write([FTMS_OP.start]).catch(e => console.warn('resume', e)); }
    this.write(encodeErg(w, this.powerRange)).catch(e => console.warn('setErg', e));
  }

  disconnect() {
    // stop request before disconnecting; without waiting if the link is already down
    this.write([FTMS_OP.stop, 0x01]).catch(() => {}).finally(() => super.disconnect());
  }
}
